import { useEffect, useState } from 'react';
import { API_CONFIG } from './apiConfig';
import { fetchWithCache } from './fetchWithCache';
import { HookResult, OverlayResult } from './types';

type PathBuilder = (mill: string, driveTimeMinutes: number) => string;

const toUrl = (path: string) => `${API_CONFIG.s3.baseUrl}/${path}`;

/**
 * Builds a hook that fetches a per-(mill, drive_time) CSV from S3, parses it,
 * and exposes `{ data, isLoading, error }`. All cancel-guarding, state reset
 * on arg change, and enabled handling live here so per-layer hooks only need
 * to supply their path builder, CSV parser, and an error-prefix string.
 */
export const createCsvStatsHook = <T>(config: {
  path: PathBuilder;
  parse: (csv: string) => T | null;
  errorPrefix: string;
}) => {
  return (
    millCode: string | null,
    driveTimeMinutes: number,
    enabled: boolean = true,
  ): HookResult<T> => {
    const [data, setData] = useState<T | null>(null);
    const [isLoading, setIsLoading] = useState(enabled && millCode !== null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      if (!enabled || !millCode) {
        setData(null);
        setIsLoading(false);
        setError(null);
        return;
      }

      const url = toUrl(config.path(millCode, driveTimeMinutes));

      let cancelled = false;
      setIsLoading(true);
      setError(null);

      fetchWithCache(url, r => r.text().then(config.parse))
        .then(parsed => {
          if (cancelled) return;
          setData(parsed);
          setIsLoading(false);
        })
        .catch(err => {
          if (cancelled) return;
          setError(`${config.errorPrefix}: ${err.message}`);
          setIsLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }, [millCode, driveTimeMinutes, enabled]);

    return { data, isLoading, error };
  };
};

/**
 * Builds a hook that resolves a precomputed PNG overlay on S3 for a given
 * (mill, drive_time). The hook fetches only the small `.meta.json` sidecar
 * to pull bounds; Leaflet's ImageOverlay loads the PNG bytes itself when
 * rendered. Returns `{ url, bounds, isLoading, error }` — null values when
 * disabled or no mill selected, so callers can render unconditionally.
 */
export const createPngMetaOverlayHook = (config: {
  metaPath: PathBuilder;
  pngPath: PathBuilder;
}) => {
  return (
    millCode: string | null,
    driveTimeMinutes: number,
    enabled: boolean = true,
  ): OverlayResult => {
    const [bounds, setBounds] = useState<[[number, number], [number, number]] | null>(null);
    const [url, setUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(enabled && millCode !== null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      if (!enabled || !millCode) {
        setBounds(null);
        setUrl(null);
        setIsLoading(false);
        setError(null);
        return;
      }

      const metaUrl = toUrl(config.metaPath(millCode, driveTimeMinutes));
      const pngUrl = toUrl(config.pngPath(millCode, driveTimeMinutes));

      let cancelled = false;
      setIsLoading(true);
      setError(null);

      fetchWithCache<{ bounds: [[number, number], [number, number]] }>(metaUrl, r => r.json())
        .then(meta => {
          if (cancelled) return;
          setBounds(meta.bounds);
          setUrl(pngUrl);
          setIsLoading(false);
        })
        .catch(err => {
          if (cancelled) return;
          setBounds(null);
          setUrl(null);
          setError(`Failed to load overlay metadata: ${err.message}`);
          setIsLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }, [millCode, driveTimeMinutes, enabled]);

    return { url, bounds, isLoading, error };
  };
};
